import type { MessageKey } from './i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ What an audit action is CALLED. One map, two screens.                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ITS OWN MODULE BECAUSE TWO SCREENS NEED IT, AND A SECOND COPY WOULD DRIFT.** The audit
 * screen renders the full trail and tenant detail renders BRS §3.11.2's extract; a duplicated map
 * means the day somebody adds `plan.retire` one of the two screens shows the raw key and nobody
 * notices which. `check.service.ts` names this project's worst case for duplication — *"the copy
 * that drifts is the one deciding what a UI offers while the server quietly disagrees"* — and this
 * is the harmless end of the same species, which is exactly when it is cheap to prevent.
 *
 * ⚠️ **The action is NOT an enum on the wire, deliberately** (see `operatorAuditEntrySchema`), so
 * `labelFor` falls back to the raw string. A newer API answering with an action this build does not
 * know renders `plan.retire` rather than a blank cell — the alternative makes the *client* the thing
 * that has to be deployed first.
 */

export const AUDIT_ACTION_LABEL: Readonly<Record<string, MessageKey>> = {
  'tenant.suspend': 'audit.action.tenantSuspend',
  'tenant.activate': 'audit.action.tenantActivate',
  'tenant.plan_assign': 'audit.action.tenantPlanAssign',
  'tenant.seat_limit_set': 'audit.action.tenantSeatLimitSet',
  'tenant.flag_set': 'audit.action.tenantFlagSet',
  'tenant.flag_clear': 'audit.action.tenantFlagClear',
  'plan.create': 'audit.action.planCreate',
  'plan.update': 'audit.action.planUpdate',
  'plan.deactivate': 'audit.action.planDeactivate',
  'job.requeue': 'audit.action.jobRequeue',
  'chore.run': 'audit.action.choreRun',
  /* ── C9 and C8 ───────────────────────────────────────────────────────── */
  /* ⚠️ The entry that OUTLIVES its subject: by the time anybody reads this label, the
     organisation it names is gone and this row is the only record it existed. */
  'tenant.delete': 'audit.action.tenantDelete',
  /* ⚠️ 0197 · the one PRODUCT action that reaches this log, for the same reason: `audit_log` went
     with the workspace, and this row is the only record it existed. Actor is `system`. */
  'workspace.deleted': 'audit.action.workspaceDeleted',
  'tenant.invite': 'audit.action.tenantInvite',
  'tenant.invite_resend': 'audit.action.tenantInviteResend',
  'tenant.contact_password_reset': 'audit.action.tenantContactReset',
  'superadmin.invite': 'audit.action.operatorInvite',
  'superadmin.suspend': 'audit.action.operatorSuspend',
  'superadmin.activate': 'audit.action.operatorActivate',
  'superadmin.delete': 'audit.action.operatorDelete',
  'superadmin.password_reset': 'audit.action.operatorPasswordReset',
  'superadmin.invite_resend': 'audit.action.operatorInviteResend',
};

export const AUDIT_RESOURCE_LABEL: Readonly<Record<string, MessageKey>> = {
  tenant: 'audit.resource.tenant',
  plan: 'audit.resource.plan',
  background_job: 'audit.resource.job',
  chore: 'audit.resource.chore',
  superadmin: 'audit.resource.operator',
  workspace: 'audit.resource.workspace',
};

/**
 * ⚠️ Takes the translator rather than calling a hook, so this stays a plain module a non-component
 * can import — and so the fallback is one line in one place rather than a `??` at every call site.
 */
export function labelFor(
  map: Readonly<Record<string, MessageKey>>,
  key: string,
  t: (message: MessageKey) => string,
): string {
  const found = map[key];
  return found === undefined ? key : t(found);
}
